import React from "react";
import { Add, CheckCircleOutline } from "@mui/icons-material";
import {
    Button,
    IconButton,
} from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";

import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";

import { useLazySendOutListFilteredForDriverQuery } from "store/services/sendOutApi";
import { useAppMeta, useAuth, useTablePagination } from "hooks";
import HighlightedText from "components/Shared/HighlightedText";
import AppTableDateTime from "components/Shared/AppTableDateTime";

const SendOutListScreenForDriver = () => {
    const { loggedInUser, isDriver } = useAuth();

    const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
        useTablePagination();

    const [searchParams] = useSearchParams();
    const keyword = searchParams.get("search") || "";

    const breadcrumbs = [{ title: "Pending Delivery" }];

    const columns = [
        {
            name: "Shipment ID",
            selector: (row) => row.shipment?.shipmentId,
            cell: (row) => (
                <Link
                    title={row.shipment?.shipmentId}
                    to={`/app/driver/shipment/${row?.shipment?._id}/detail`}
                    className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
                >
                    {row.shipment?.shipmentId ? (
                        <HighlightedText text={row.shipment?.shipmentId} highlight={keyword} />
                    ) : (
                        "N/A"
                    )}
                </Link>
            ),
        },
        {
            name: "Send Out ID",
            selector: (row) => row?.sendOutId,
            cell: (row) => {
                const displayId = row?.sendOutId;
                const path = !isDriver
                    ? `/app/crm/sendOut/${row?._id}/detail`
                    : `/app/driver/sendOut/${row?._id}/detail`;

                return displayId ? (
                    <Link
                        title={displayId}
                        to={path}
                        className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
                    >
                        <HighlightedText text={displayId} highlight={keyword} />
                    </Link>
                ) : (
                    "N/A"
                );
            },
        },
        {
            name: "Receiver Name",
            selector: (row) => row.shipment?.receiver?.name,
            cell: (row) => row.shipment?.receiver?.name || "N/A",
        },
        {
            name: "Receiver Phone",
            selector: (row) => row.shipment?.receiver?.phone,
            cell: (row) => row.shipment?.receiver?.phone || "N/A",
        },
        {
            name: "Created Date",
            selector: (row) => <AppTableDateTime date={row?.sendOutData?.createdAt} />,
        },
        {
            name: "Actions",
            cell: (row) => {
                const linkState = {
                    sendOutId: { _id: row._id, sendOutId: row.sendOutId },
                    shipmentId: [
                        {
                            _id: row.shipment?._id,
                            shipmentId: row.shipment?.shipmentId,
                            receiver: row.shipment?.receiver?._id || `receiver_${row.shipment?._id}`,
                            receiverDetails: {
                                name: row.shipment?.receiver?.name || "Unknown",
                                phone: row.shipment?.receiver?.phone || "N/A",
                                address: row.shipment?.receiver?.address || "N/A",
                                email: row.shipment?.receiver?.email || "N/A",
                            },
                            deliverySecurity: row.shipment?.deliverySecurity || "Normal",
                            customerDetails: row.shipment?.customerDetails || null,
                            vendorDetails: row.shipment?.vendorDetails || null,
                            remainingShipmentAmount: row.shipment?.remainingShipmentAmount || 0,
                        },
                    ],
                };
                return (

                    <Link
                        to="/app/driver/pendingDelivery/delivery"
                        state={linkState}
                    >
                        <IconButton size="small" aria-label="Complete Delivery" color="success">
                            <CheckCircleOutline />
                        </IconButton>
                    </Link>
                );
            },
            ignoreRowClick: true,
            right: true,
        },
    ];

    const [fetchSendOuts, { data: sendOutRes, isLoading, isFetching, error }] =
        useLazySendOutListFilteredForDriverQuery();

    React.useEffect(() => {
        fetchSendOuts({
            page: currentPage,
            limit: perPage,
            ...(keyword && { keyword }),
            driverId: loggedInUser.typeRef,
        });
    }, [ currentPage, fetchSendOuts, keyword, perPage, loggedInUser]);

    useAppMeta(`Send Outs`);

    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
            <BreadcrumbLayout.Error error={error} />
            <BreadcrumbLayout.Paper>
                <TableFilter
                    searchLabel="Send Out ID"
                    disabled={isLoading || isFetching}
                ></TableFilter>
                <AppTable
                    columns={columns}
                    data={sendOutRes?.data ?? []}
                    progressPending={isLoading || isFetching}
                    paginationServer
                    paginationPerPage={perPage}
                    paginationTotalRows={sendOutRes?.totalItems ?? 0}
                    paginationDefaultPage={currentPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                />
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

export default SendOutListScreenForDriver;

const PageActions = () => {
    const { isDriver } = useAuth();
    return (
        <>
            {!isDriver && (
                <Link to="/app/crm/sendOut/create">
                    <Button startIcon={<Add />} variant="contained" color="primary">
                        Add Send Out
                    </Button>
                </Link>
            )}
        </>
    );
};